import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSplitViewAwareParams } from '@/hooks/useSplitViewAwareParams';
import { useTabTitleSync } from '@/hooks/useTabTitleSync';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Connection,
  Node,
  Edge,
  ReactFlowProvider,
  useReactFlow,
  Panel,
  NodeTypes,
  EdgeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { ObjectNode } from '@/components/logic/ObjectNode';
import { DialogNode } from '@/components/logic/DialogNode';
import { LogicFlowNode } from '@/components/logic/LogicFlowNode';
import { ObjectNodeExpanded } from '@/components/logic/ObjectNodeExpanded';
import { DialogNodeExpanded } from '@/components/logic/DialogNodeExpanded';
import { LogicFlowNodeExpanded } from '@/components/logic/LogicFlowNodeExpanded';
import { CustomEdge, EdgeType } from '@/components/logic/CustomEdge';
import { NodeDetailsPanel } from '@/components/logic/NodeDetailsPanel';
import { Button } from '@/components/ui/button';
import IconProvider from '@/components/ui/IconProvider';
import { useToast } from '@/hooks/use-toast';
import { useZoom } from '@/providers/ZoomProvider';
import { usePageAccessGuard } from '@/hooks/usePageAccessGuard';
import {
  fetchLogicFlow,
  updateLogicFlow,
  LogicFlow,
  LogicFlowDataContent,
} from '@/lib/stores/logicFlowStore';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { Label } from '@/components/ui/label';
import { UnifiedWizardHeader } from '@/components/wizards/shared/UnifiedWizardHeader';
import { useItemLock } from '@/hooks/useItemLock';
import { LockStatusBanner } from '@/components/ui/LockStatusBanner';

const nodeTypes: NodeTypes = {
  object: ObjectNode,
  dialog: DialogNode,
  flow: LogicFlowNode,
  objectExpanded: ObjectNodeExpanded,
  dialogExpanded: DialogNodeExpanded,
  flowExpanded: LogicFlowNodeExpanded,
};

const edgeTypes = {
  custom: CustomEdge,
} as EdgeTypes;

function LogicFlowDetailPageContent() {
  usePageAccessGuard('logicFlows');
  const { flowId } = useSplitViewAwareParams<{ flowId: string }>();
  const navigate = useNavigate();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();
  const reactFlowInstance = useReactFlow();
  const { zoom } = useZoom();
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [currentFlow, setCurrentFlow] = useState<LogicFlow | null>(null);
  const [connectionType, setConnectionType] = useState<EdgeType>('custom');
  const [isLoading, setIsLoading] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Store original state for cancel operation
  const [originalNodes, setOriginalNodes] = useState<Node[]>([]);
  const [originalEdges, setOriginalEdges] = useState<Edge[]>([]);

  // Item lock system for collaborative editing
  const {
    lockStatus,
    canEdit,
    isAcquiring: isAcquiringLock,
    lockError,
    isLockedByOther,
    refreshLockStatus,
    forceLock,
  } = useItemLock({
    itemId: flowId,
    itemType: 'flow',
    gameId: selectedGame?.id,
    autoAcquire: true,
    isEditMode: true, // Flows are always in edit mode when opened
    onLockLost: () => {
      toast({
        title: 'Edit lock lost',
        description: 'Another user has taken over editing. Your changes may not be saved.',
        variant: 'destructive',
      });
    },
  });
  
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  
  // Sync tab title with flow name
  useTabTitleSync(currentFlow?.name);
  
  // Calculate inverse zoom to counteract parent zoom
  const inverseZoom = 100 / zoom;

  // Fetch flow on mount
  useEffect(() => {
    if (!selectedGame?.id || !flowId) return;
    
    // Reset hasLoadedRef when flowId changes to allow loading new flow
    hasLoadedRef.current = false;
    
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) return;

    const loadFlow = async () => {
      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        const flow = await fetchLogicFlow(flowId, selectedGame.id, toast);
        setCurrentFlow(flow);
        
        // Add onToggleExpand callback to loaded nodes
        const nodesWithCallbacks = (flow.data.nodes || []).map((node: Node) => ({
          ...node,
          data: {
            ...node.data,
            onToggleExpand: () => {
              setNodes((nds) =>
                nds.map((n) => {
                  if (n.id === node.id) {
                    const isCurrentlyExpanded = n.data.expanded;
                    const baseType = n.type === 'objectExpanded' ? 'object' : 
                                    n.type === 'dialogExpanded' ? 'dialog' : 
                                    n.type === 'flowExpanded' ? 'flow' :
                                    n.type;
                    
                    let newType: string;
                    if (baseType === 'object') {
                      newType = isCurrentlyExpanded ? 'object' : 'objectExpanded';
                    } else if (baseType === 'dialog') {
                      newType = isCurrentlyExpanded ? 'dialog' : 'dialogExpanded';
                    } else if (baseType === 'flow') {
                      newType = isCurrentlyExpanded ? 'flow' : 'flowExpanded';
                    } else {
                      newType = n.type || 'object';
                    }
                    
                    return {
                      ...n,
                      type: newType,
                      data: {
                        ...n.data,
                        expanded: !isCurrentlyExpanded,
                      },
                    };
                  }
                  return n;
                })
              );
            },
          },
        }));
        
        setNodes(nodesWithCallbacks);
        setEdges(flow.data.edges || []);
        
        // Store original state for cancel operation
        setOriginalNodes(nodesWithCallbacks);
        setOriginalEdges(flow.data.edges || []);
        
        // Restore viewport - defer until ReactFlow is ready
        setTimeout(() => {
          if (flow.data.viewport && reactFlowInstance) {
            try {
              reactFlowInstance.setViewport(flow.data.viewport);
            } catch (error) {
              console.warn('Failed to restore viewport:', error);
            }
          }
        }, 100);
        
        // Defer setting hasLoadedRef to ensure change detection doesn't 
        // trigger on initial load
        setTimeout(() => {
          hasLoadedRef.current = true;
        }, 0);
      } catch (error) {
        console.error('Failed to load logic flow:', error);
        toast({
          title: 'Error',
          description: 'Failed to load logic flow',
          variant: 'destructive',
        });
        navigate('/logic');
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    };

    loadFlow();
  }, [selectedGame?.id, flowId, toast, navigate]);

  // Save function
  const saveFlow = useCallback(async () => {
    if (!currentFlow || !selectedGame?.id) return;

    setIsSaving(true);
    try {
      const viewport = reactFlowInstance.getViewport();
      const flowData: LogicFlowDataContent = {
        nodes,
        edges,
        viewport,
      };

      await updateLogicFlow({
        id: currentFlow.id,
        game_id: selectedGame.id,
        data: flowData,
      });

      // Update original state after successful save
      setOriginalNodes(nodes);
      setOriginalEdges(edges);
      setHasUnsavedChanges(false);

      toast({
        title: 'Success',
        description: 'Logic flow saved successfully',
      });
    } catch (error) {
      console.error('Failed to save logic flow:', error);
      toast({
        title: 'Error',
        description: 'Failed to save logic flow',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }, [currentFlow, selectedGame?.id, nodes, edges, reactFlowInstance, toast]);

  // Track changes when nodes or edges update (but not during initial load)
  useEffect(() => {
    if (currentFlow && hasLoadedRef.current) {
      // Check if nodes or edges actually changed
      const nodesChanged = JSON.stringify(nodes) !== JSON.stringify(originalNodes);
      const edgesChanged = JSON.stringify(edges) !== JSON.stringify(originalEdges);
      setHasUnsavedChanges(nodesChanged || edgesChanged);
    }
  }, [nodes, edges, currentFlow, originalNodes, originalEdges]);

  // Handle toggle expand/collapse for nodes
  const handleToggleExpand = useCallback((nodeId: string) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          const isCurrentlyExpanded = node.data.expanded;
          const baseType = node.type === 'objectExpanded' ? 'object' : 
                          node.type === 'dialogExpanded' ? 'dialog' : 
                          node.type === 'flowExpanded' ? 'flow' :
                          node.type;
          
          let newType: string;
          if (baseType === 'object') {
            newType = isCurrentlyExpanded ? 'object' : 'objectExpanded';
          } else if (baseType === 'dialog') {
            newType = isCurrentlyExpanded ? 'dialog' : 'dialogExpanded';
          } else if (baseType === 'flow') {
            newType = isCurrentlyExpanded ? 'flow' : 'flowExpanded';
          } else {
            newType = node.type || 'object';
          }
          
          return {
            ...node,
            type: newType,
            data: {
              ...node.data,
              expanded: !isCurrentlyExpanded,
              onToggleExpand: () => handleToggleExpand(nodeId),
            },
          };
        }
        return node;
      })
    );
  }, [setNodes]);

  // Cancel function - revert to original state
  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      // Restore original nodes with callbacks
      const nodesWithCallbacks = originalNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          onToggleExpand: () => handleToggleExpand(node.id),
        },
      }));
      
      setNodes(nodesWithCallbacks);
      setEdges(originalEdges);
      setHasUnsavedChanges(false);
      
      toast({
        title: 'Changes discarded',
        description: 'Reverted to last saved state',
      });
    }
    
    navigate('/logic');
  }, [hasUnsavedChanges, originalNodes, originalEdges, setNodes, setEdges, navigate, toast, handleToggleExpand]);

  // Handle drag over
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop with validation
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const data = event.dataTransfer.getData('application/reactflow');
      if (!data) return;

      try {
        const parsedData = JSON.parse(data);
        
        // Validate: Only allow 'glossary' (objects), 'dialog', and 'flow' items
        if (parsedData.itemType !== 'glossary' && parsedData.itemType !== 'dialog' && parsedData.itemType !== 'flow') {
          toast({
            title: 'Cannot drop item',
            description: `${parsedData.itemType === 'blueprint' ? 'Templates' : 'This item type'} cannot be added to the logic canvas. Only objects, dialogs, and logic flows are supported.`,
            variant: 'destructive',
          });
          return;
        }

        // Prevent adding a logic flow to itself
        if (parsedData.itemType === 'flow' && currentFlow && parsedData.itemId === currentFlow.id) {
          toast({
            title: 'Cannot add flow',
            description: 'A logic flow cannot contain itself to prevent infinite recursion.',
            variant: 'destructive',
          });
          return;
        }

        const position = reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        // Map itemType to node type
        let nodeType: string;
        if (parsedData.itemType === 'glossary') {
          nodeType = 'object';
        } else if (parsedData.itemType === 'dialog') {
          nodeType = 'dialog';
        } else if (parsedData.itemType === 'flow') {
          nodeType = 'flow';
        } else {
          nodeType = 'object'; // fallback
        }

        const nodeId = `${nodeType}-${parsedData.itemId}-${Date.now()}`;
        const newNode: Node = {
          id: nodeId,
          type: nodeType,
          position,
          data: {
            itemId: parsedData.itemId,
            itemType: nodeType,
            label: parsedData.label,
            expanded: false,
            onToggleExpand: () => handleToggleExpand(nodeId),
          },
        };

        setNodes((nds) => [...nds, newNode]);
        
        toast({
          title: 'Item added',
          description: `${parsedData.label} has been added to the canvas`,
        });
      } catch (error) {
        console.error('Failed to parse drop data:', error);
        toast({
          title: 'Error',
          description: 'Failed to add item to canvas',
          variant: 'destructive',
        });
      }
    },
    [reactFlowInstance, setNodes, toast]
  );

  // Handle connection with validation
  const onConnect = useCallback(
    (params: Connection) => {
      const sourceHandle = params.sourceHandle;
      const targetHandle = params.targetHandle;
      
      const validConnections: Record<string, string[]> = {
        'right': ['left'],
        'bottom': ['top'],
        'left': ['right'],
        'top': ['bottom'],
      };
      
      if (sourceHandle && targetHandle) {
        const validTargets = validConnections[sourceHandle];
        if (!validTargets || !validTargets.includes(targetHandle)) {
          toast({
            title: 'Invalid connection',
            description: `Cannot connect ${sourceHandle} to ${targetHandle}. Connect opposite handles (e.g., right to left, bottom to top).`,
            variant: 'destructive',
          });
          return;
        }
      }

      const newEdge: Edge = {
        ...params,
        id: `edge-${Date.now()}`,
        type: 'custom',
        data: {
          type: connectionType,
          label: '',
        },
      } as Edge;

      setEdges((eds) => addEdge(newEdge, eds));
      
      toast({
        title: 'Connection created',
        description: `Connected ${sourceHandle} to ${targetHandle}`,
      });
    },
    [connectionType, setEdges, toast]
  );

  // Handle node selection
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
    setIsPanelOpen(true);
  }, []);

  // Handle edge selection
  const onEdgeClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
    setIsPanelOpen(true);
  }, []);

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  // Delete node
  const handleDeleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    setIsPanelOpen(false);
  }, [setNodes, setEdges]);

  // Delete edge
  const handleDeleteEdge = useCallback((edgeId: string) => {
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    setSelectedEdge(null);
    setIsPanelOpen(false);
  }, [setEdges]);

  // Update edge label
  const handleUpdateEdgeLabel = useCallback((edgeId: string, label: string) => {
    setEdges((eds) =>
      eds.map((e) =>
        e.id === edgeId
          ? { ...e, data: { ...e.data, label } }
          : e
      )
    );
  }, [setEdges]);

  if (!selectedGame) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <IconProvider iconstring="fa-circle-info" size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg text-muted-foreground">Please select a game to use Logic</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <IconProvider iconstring="fa-spinner" size={48} className="mx-auto mb-4 opacity-50 animate-spin" />
          <p className="text-lg text-muted-foreground">Loading logic flow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full" style={{ zoom: `${inverseZoom}%` }}>
      {/* Lock Status Banner */}
      {isLockedByOther && (
        <div className="px-6 py-4">
          <LockStatusBanner
            lockStatus={lockStatus}
            isAcquiring={isAcquiringLock}
            lockError={lockError}
            canEdit={canEdit}
            onRefresh={refreshLockStatus}
            onForceTakeover={async () => {
              const success = await forceLock();
              if (success) {
                toast({
                  title: 'Edit access acquired',
                  description: 'You now have edit access to this flow.',
                });
              }
            }}
            canForceTakeover={true}
            size="md"
          />
        </div>
      )}

      {/* Header */}
      <div className="px-6 border-b bg-background">
        <UnifiedWizardHeader
          title={currentFlow?.name || 'Logic Flow'}
          mode="edit"
          itemType="dialogs"
          onCancel={handleCancel}
          hasUnsavedChanges={hasUnsavedChanges}
          onSubmit={saveFlow}
          submitText="Save"
          canEdit={canEdit}
        />
      </div>

      {/* Main Canvas */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onPaneClick={onPaneClick}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          className="bg-background"
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>

      {/* Details Panel */}
      {isPanelOpen && (
        <NodeDetailsPanel
          selectedNode={selectedNode}
          selectedEdge={selectedEdge}
          onDeleteNode={handleDeleteNode}
          onDeleteEdge={handleDeleteEdge}
          onUpdateEdgeLabel={handleUpdateEdgeLabel}
          onClose={() => setIsPanelOpen(false)}
        />
      )}
    </div>
  );
}

export default function LogicFlowDetailPage() {
  return (
    <ReactFlowProvider>
      <LogicFlowDetailPageContent />
    </ReactFlowProvider>
  );
}

